import { Cookie } from '../helpers'
import { queryParamHas } from '../helpers/getQueryParam'

/** Possible statuses for CCPA */
enum CCPA {
  yes = '1',
  no = '0'
}

/** Available commands for the USP API */
enum Command {
  getUSPData = 'getUSPData'
}

/** Possible statuses for the UPS string */
enum Status {
  yes = 'Y',
  no = 'N',
  notApplicable = '-'
}

const isCcpaTest = queryParamHas('test', 'ccpa')

/**
 * Public API exposed on window. Allows third-parties to
 * call window.__uspapi to retrieve data on the user's CCPA/US Privacy status.
 * Spec found here:
 * https://iabtechlab.com/wp-content/uploads/2019/11/US-Privacy-USER-SIGNAL-API-SPEC-v1.0.pdf
 */
export class USP {
  static readonly VERSION = 1
  private static ccpaStatus: CCPA = CCPA.no
  private static explicitNoticeStatus: Status = Status.notApplicable
  private static userOptOutStatus: Status = Status.notApplicable
  private static lspaStatus: Status = Status.notApplicable
  private static cookie: Cookie<string> = new Cookie('usprivacy')

  static init({ ccpa }: IWebViewModel) {
    USP.ccpaStatus = ccpa as CCPA
    window.__uspapi = USP.__uspapi
    window.addEventListener('message', USP.handlePostMessage)

    const cookieValue = USP.cookie.value
    const hasUSPCookie =
      cookieValue && cookieValue[0] === USP.VERSION.toString()

    // set USP.uspString with existing cookie values
    if (hasUSPCookie) {
      USP.explicitNoticeStatus = cookieValue[1] as Status
      USP.userOptOutStatus = cookieValue[2] as Status
      // eslint-disable-next-line no-magic-numbers
      USP.lspaStatus = cookieValue[3] as Status
    }

    // set USP.uspString and cookie for a user with no USP cookie and CCPA/CCPA test
    if (!hasUSPCookie && (USP.ccpaStatus === CCPA.yes || isCcpaTest)) {
      this.ccpaAppliesToUser()
    }

    // override USP.uspString for a user with USP cookie but no CCPA/CCPA test
    if (hasUSPCookie && USP.ccpaStatus === CCPA.no && !isCcpaTest) {
      this.userHasCookieButNotInCA()
    }
  }

  /** Public Method exposed on the window for third parties */
  static __uspapi(command: string, version: 1, callback: Function): void {
    if (isValidApiRequest({ command, version })) {
      callback(getUspObject(USP.uspString), true)
    }
  }

  static ccpaAppliesToUser() {
    USP.userOptOutStatus = Status.no
    USP.explicitNoticeStatus = Status.no
    USP.lspaStatus = Status.yes
    USP.cookie.set(USP.uspString)
  }

  /** Doesn't overwrite any existing cookies */
  static userHasCookieButNotInCA() {
    USP.userOptOutStatus = Status.notApplicable
    USP.explicitNoticeStatus = Status.notApplicable
    USP.lspaStatus = Status.notApplicable
  }

  static userHasReceivedExplictNotice() {
    USP.ccpaAppliesToUser()
    USP.explicitNoticeStatus = Status.yes
    USP.cookie.set(USP.uspString)
  }

  static userHasOptedOut() {
    USP.userHasReceivedExplictNotice()
    USP.userOptOutStatus = Status.yes
    USP.cookie.set(USP.uspString)
  }

  /** Handles requests sent from within iframes/safeframes */
  private static handlePostMessage(event: MessageEvent): void {
    let message
    try {
      message = JSON.parse(event.data)
    } catch {
      /** no-op */
    }

    if (isValidMessage(message)) {
      const uspReturnMessage = JSON.stringify(
        getReturnMessage(message, USP.uspString)
      )
      // @ts-ignore Typing is possibly broken??
      event.source.postMessage(uspReturnMessage, event.source)
    }
  }

  public static get uspString(): string {
    const { VERSION, explicitNoticeStatus, userOptOutStatus, lspaStatus } = USP
    return `${VERSION}${explicitNoticeStatus}${userOptOutStatus}${lspaStatus}`
  }
}

const isValidApiRequest = ({ command, version }): boolean =>
  command === Command.getUSPData && version === USP.VERSION

const isValidMessage = (message: IUSPMessageCall): boolean =>
  !!message &&
  message.command === Command.getUSPData &&
  message.version === USP.VERSION &&
  message.callId

const getUspObject = (uspString: string) => ({
  version: USP.VERSION,
  uspString
})

const getReturnMessage = (
  message: IUSPMessageCall,
  uspString: string
): IUSPMessageReturn => ({
  __uspapiReturn: {
    returnValue: uspString,
    callId: message.callId,
    success: true
  }
})

interface IUSPMessageCall {
  command: string
  version: number
  callId: any
}

interface IUSPMessageReturn {
  __uspapiReturn: {
    returnValue: string
    success: boolean
    callId: string
  }
}
